import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Copy, Loader2, Plus, Trash2 } from "lucide-react";

import {
  type CostBasis,
  type CostParameterWrite,
  type GasPurchase,
  gasPurchasesApi,
  parametersApi,
} from "@/api/parameters";
import { type SolarPlant, type SolarTariff, solarApi } from "@/api/solar";
import { useCanEdit } from "@/hooks/useRole";
import { fmt, monthLabel, num } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card";

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** Зсунути YYYY-MM на N місяців. shift=−1 → попередній, +1 → наступний. */
function shiftMonth(iso: string, shift: number): string {
  const [y, m] = iso.split("-").map(Number);
  const d = new Date(y, (m - 1) + shift, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

interface Row {
  code: string;
  name: string;
  value: string;
  /** Ключ одиниці: 'month' | 'day' | 'hour' | 'm3'. Визначає і basis, і підпис. */
  unitKey: UnitKey;
  quantity: string;
  notes: string;
}

type UnitKey = CostBasis | "m3";

/** Одиниця — не підпис, а справжній множник: `basis` каже движку, за який
 *  період задано значення (FR-FIN-02). 'm3' — окремий випадок для gas_price:
 *  там грн/м³ множиться на витрату, а не ділиться на час. */
const UNIT_OPTIONS: { key: UnitKey; unit: string }[] = [
  { key: "month", unit: "грн/місяць" },
  { key: "day", unit: "грн/день" },
  { key: "hour", unit: "грн/год" },
  { key: "m3", unit: "грн/м³" },
];

const unitLabel = (k: UnitKey): string =>
  UNIT_OPTIONS.find((o) => o.key === k)?.unit ?? "грн/місяць";

/** Зворотне мапування рядка з БД у ключ одиниці. */
const unitKeyOf = (code: string, unit: string, basis: CostBasis): UnitKey =>
  code === "gas_price" || unit.includes("м³") ? "m3" : basis;

const toWrite = (r: Row): CostParameterWrite => ({
  code: r.code,
  name: r.name,
  value: Number(r.value),
  unit: unitLabel(r.unitKey),
  quantity: Number(r.quantity || "1"),
  // 'm3' — не часова одиниця; basis для gas_price движок ігнорує.
  basis: r.unitKey === "m3" ? "month" : r.unitKey,
  notes: r.notes || null,
});

const SUGGESTED_KGU: { code: string; name: string; unitKey: UnitKey }[] = [
  { code: "gas_price", name: "Ціна газу", unitKey: "m3" },
  { code: "rent", name: "Оренда", unitKey: "month" },
  { code: "service", name: "Обслуговування", unitKey: "month" },
  { code: "consumables", name: "Витратні матеріали", unitKey: "month" },
  { code: "sga", name: "SG&A (загальнофірмові)", unitKey: "month" },
];

const SUGGESTED_PLANT: { code: string; name: string; unitKey: UnitKey }[] = [
  { code: "rent", name: "Оренда", unitKey: "month" },
  { code: "service", name: "Обслуговування", unitKey: "month" },
];

export function DataPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canEdit = useCanEdit();

  const [month, setMonth] = useState(currentMonth());

  const plants = useQuery({
    queryKey: ["solar-plants"],
    queryFn: () => solarApi.listPlants(),
  });

  const copyPrev = useMutation({
    mutationFn: () => parametersApi.copyFromPreviousMonth(month),
    // Копіюються всі скоупи одразу — і КГУ, і статті кожної станції.
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cost-parameters"] }),
  });

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-2xl font-semibold text-brand-navy-700">{t("data_page.title")}</h1>
        <p className="text-sm text-slate-500">{t("data_page.subtitle")}</p>
        {!canEdit && <div className="mt-2 text-xs text-amber-600">{t("common.read_only")}</div>}
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-1">
              <label className="text-sm text-slate-700 mr-1">{t("data_page.month")}:</label>
              <Button variant="outline" size="sm"
                onClick={() => setMonth((m) => shiftMonth(m, -1))}
                aria-label={t("data_page.prev_month")} title={t("data_page.prev_month")}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Input type="month" value={month}
                onChange={(e) => setMonth(e.target.value)} className="w-44" />
              <Button variant="outline" size="sm"
                onClick={() => setMonth((m) => shiftMonth(m, 1))}
                aria-label={t("data_page.next_month")} title={t("data_page.next_month")}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            {canEdit && (
              <Button variant="outline" size="sm"
                onClick={() => {
                  if (window.confirm(t("data_page.copy_from_previous_confirm"))) {
                    copyPrev.mutate();
                  }
                }}
                disabled={copyPrev.isPending}>
                <Copy className="h-4 w-4" /> {t("data_page.copy_from_previous")}
              </Button>
            )}
          </div>
          <CardDescription>{t("data_page.common_items_hint")}</CardDescription>
          {copyPrev.isError && (
            <div className="text-sm text-red-600">{t("common.error")}</div>
          )}
        </CardHeader>
      </Card>

      {/* Скоуп КГУ + загальнофірмові статті (plant_id IS NULL). */}
      <CostParamsTable
        month={month}
        canEdit={canEdit}
        title={t("data_page.scope_kgu")}
        hint={t("data_page.scope_kgu_hint")}
        suggested={SUGGESTED_KGU}
      />

      {/* Статті кожної СЕС: оренда даху/землі, обслуговування (FR-FIN-01). */}
      {(plants.data ?? []).map((p) => (
        <CostParamsTable
          key={p.id}
          month={month}
          plantId={p.id}
          canEdit={canEdit}
          title={t("data_page.scope_plant", { plant: p.name })}
          hint={t("data_page.scope_plant_hint")}
          suggested={SUGGESTED_PLANT}
        />
      ))}

      <GasPurchasesCard canEdit={canEdit} />
      <SolarTariffsCard canEdit={canEdit} />
    </div>
  );
}

// ---------- Таблиця статей витрат одного скоупу ----------

function CostParamsTable({
  month,
  plantId,
  canEdit,
  title,
  hint,
  suggested,
}: {
  month: string;
  plantId?: string;
  canEdit: boolean;
  title: string;
  hint: string;
  suggested: { code: string; name: string; unitKey: UnitKey }[];
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const queryKey = ["cost-parameters", month, plantId ?? "kgu"];

  const items = useQuery({
    queryKey,
    queryFn: () => parametersApi.getCostParameters(month, plantId),
  });

  const [rows, setRows] = useState<Row[]>([]);

  useEffect(() => {
    if (!items.data) return;
    if (items.data.length > 0) {
      setRows(
        items.data.map((it) => ({
          code: it.code,
          name: it.name,
          value: String(it.value),
          unitKey: unitKeyOf(it.code, it.unit, it.basis),
          quantity: String(it.quantity),
          notes: it.notes ?? "",
        })),
      );
    } else {
      // Pre-seed типовими статтями для зручності першого вводу.
      setRows(suggested.map((s) => ({ ...s, value: "", quantity: "1", notes: "" })));
    }
  }, [items.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const setRow = (i: number, patch: Partial<Row>) =>
    setRows((s) => s.map((r, j) => (i === j ? { ...r, ...patch } : r)));

  const save = useMutation({
    mutationFn: () =>
      parametersApi.putCostParameters(
        month,
        rows.filter((r) => r.code && r.name && r.value !== "").map(toWrite),
        plantId,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });

  const codesUnique =
    new Set(rows.map((r) => r.code).filter(Boolean)).size ===
    rows.filter((r) => r.code).length;
  const valid =
    rows.length > 0 &&
    rows.every((r) => r.code && r.name && r.value !== "") &&
    codesUnique;

  return (
    <Card>
      <CardHeader>
        <h2 className="text-lg font-semibold text-brand-navy-700">{title}</h2>
        <CardDescription>{hint}</CardDescription>
      </CardHeader>
      <CardContent>
        {items.isLoading ? (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500">
                  <th className="p-2 w-[20%]">{t("data_page.code")}</th>
                  <th className="p-2 w-[25%]">{t("data_page.name")}</th>
                  <th className="p-2 w-[15%]">{t("data_page.value")}</th>
                  <th className="p-2 w-[15%]">{t("data_page.unit")}</th>
                  <th className="p-2 w-[10%]">{t("data_page.quantity")}</th>
                  <th className="p-2">{t("data_page.notes")}</th>
                  {canEdit && <th className="p-2 w-12" />}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100 align-top">
                    <td className="p-2">
                      <Input value={r.code} disabled={!canEdit} placeholder="rent"
                        onChange={(e) => setRow(i, { code: e.target.value.toLowerCase() })} />
                    </td>
                    <td className="p-2">
                      <Input value={r.name} disabled={!canEdit}
                        onChange={(e) => setRow(i, { name: e.target.value })} />
                    </td>
                    <td className="p-2">
                      <Input type="number" step="0.0001" value={r.value} disabled={!canEdit}
                        onChange={(e) => setRow(i, { value: e.target.value })} />
                    </td>
                    <td className="p-2">
                      <select
                        className="w-full h-10 rounded-md border border-slate-300 bg-white px-2 text-sm disabled:bg-slate-50"
                        value={r.unitKey}
                        disabled={!canEdit}
                        onChange={(e) => setRow(i, { unitKey: e.target.value as UnitKey })}
                      >
                        {UNIT_OPTIONS.map((o) => (
                          <option key={o.key} value={o.key}>{o.unit}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-2">
                      <Input type="number" step="0.01" value={r.quantity} disabled={!canEdit}
                        onChange={(e) => setRow(i, { quantity: e.target.value })} />
                    </td>
                    <td className="p-2">
                      <Input value={r.notes} disabled={!canEdit}
                        onChange={(e) => setRow(i, { notes: e.target.value })} />
                    </td>
                    {canEdit && (
                      <td className="p-2">
                        <Button variant="ghost" size="icon"
                          onClick={() => setRows((s) => s.filter((_, j) => j !== i))}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>

            {rows.length === 0 && (
              <div className="text-sm text-slate-500 py-4">{t("data_page.no_items")}</div>
            )}
          </div>
        )}

        {canEdit && (
          <div className="flex items-center gap-3 mt-4 flex-wrap">
            <Button variant="outline"
              onClick={() =>
                setRows((s) => [
                  ...s,
                  { code: "", name: "", value: "", unitKey: "month", quantity: "1", notes: "" },
                ])
              }>
              <Plus className="h-4 w-4" /> {t("data_page.add_row")}
            </Button>
            <Button onClick={() => save.mutate()} disabled={!valid || save.isPending}>
              {save.isPending ? t("common.saving") : t("common.save")}
            </Button>
            {save.isSuccess && <span className="text-sm text-brand-mint-600">{t("common.saved")}</span>}
            {save.isError && <span className="text-sm text-red-600">{t("common.error")}</span>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ---------- Тарифи СЕС (FR-SOLAR-04) ----------

interface TariffRow {
  effective_from: string;
  price: string;
  buy_price: string;
}

function SolarTariffsCard({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const plants = useQuery({
    queryKey: ["solar-plants"],
    queryFn: () => solarApi.listPlants(),
  });

  if (plants.isLoading) {
    return (
      <Card>
        <CardContent className="p-4 flex items-center gap-2 text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}
        </CardContent>
      </Card>
    );
  }
  if ((plants.data ?? []).length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <h2 className="text-lg font-semibold text-brand-navy-700">
          {t("data_page.solar_tariffs")}
        </h2>
        <CardDescription>{t("data_page.solar_tariffs_hint")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {(plants.data ?? []).map((p) => (
          <PlantTariffs key={p.id} plant={p} canEdit={canEdit} />
        ))}
      </CardContent>
    </Card>
  );
}

const toTariffRow = (x: SolarTariff): TariffRow => ({
  effective_from: x.effective_from,
  price: String(x.price_uah_per_kwh),
  buy_price: x.buy_price_uah_per_kwh === null ? "" : String(x.buy_price_uah_per_kwh),
});

function PlantTariffs({ plant, canEdit }: { plant: SolarPlant; canEdit: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const tariffs = useQuery({
    queryKey: ["solar-tariffs", plant.id],
    queryFn: () => solarApi.listTariffs(plant.id),
  });

  const [rows, setRows] = useState<TariffRow[]>([]);
  useEffect(() => {
    if (tariffs.data) setRows(tariffs.data.map(toTariffRow));
  }, [tariffs.data]);

  const setRow = (i: number, patch: Partial<TariffRow>) =>
    setRows((s) => s.map((r, j) => (i === j ? { ...r, ...patch } : r)));

  const save = useMutation({
    mutationFn: () =>
      solarApi.putTariffs(
        plant.id,
        rows
          .filter((r) => r.effective_from && r.price !== "")
          .map((r) => ({
            effective_from: r.effective_from,
            price_uah_per_kwh: Number(r.price),
            buy_price_uah_per_kwh: r.buy_price === "" ? null : Number(r.buy_price),
          })),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["solar-tariffs", plant.id] });
      qc.invalidateQueries({ queryKey: ["solar-plants"] });
    },
  });

  const datesUnique =
    new Set(rows.map((r) => r.effective_from).filter(Boolean)).size ===
    rows.filter((r) => r.effective_from).length;
  const valid = rows.every((r) => r.effective_from && r.price !== "") && datesUnique;

  return (
    <div>
      <div className="text-sm font-medium text-slate-700 mb-2">
        {t("data_page.solar_plant")}: {plant.name}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500">
              <th className="p-2 w-[25%]">{t("data_page.solar_effective_from")}</th>
              <th className="p-2 w-[30%]">{t("data_page.solar_price")}</th>
              <th className="p-2 w-[30%]">{t("data_page.solar_buy_price")}</th>
              {canEdit && <th className="p-2 w-12" />}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="p-2">
                  <Input type="date" value={r.effective_from} disabled={!canEdit}
                    onChange={(e) => setRow(i, { effective_from: e.target.value })} />
                </td>
                <td className="p-2">
                  <Input type="number" step="0.0001" min="0" value={r.price} disabled={!canEdit}
                    onChange={(e) => setRow(i, { price: e.target.value })} />
                </td>
                <td className="p-2">
                  <Input type="number" step="0.0001" min="0" value={r.buy_price} disabled={!canEdit}
                    onChange={(e) => setRow(i, { buy_price: e.target.value })} />
                </td>
                {canEdit && (
                  <td className="p-2">
                    <Button variant="ghost" size="icon"
                      onClick={() => setRows((s) => s.filter((_, j) => j !== i))}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div className="text-sm text-slate-500 py-3">{t("data_page.solar_no_tariffs")}</div>
        )}
      </div>
      {canEdit && (
        <div className="flex items-center gap-3 mt-3 flex-wrap">
          <Button variant="outline" size="sm"
            onClick={() =>
              setRows((s) => [
                ...s,
                {
                  effective_from: new Date().toISOString().slice(0, 10),
                  price: "",
                  buy_price: "",
                },
              ])
            }>
            <Plus className="h-4 w-4" /> {t("data_page.solar_add_tariff")}
          </Button>
          <Button size="sm" onClick={() => save.mutate()} disabled={!valid || save.isPending}>
            {save.isPending ? t("common.saving") : t("common.save")}
          </Button>
          {save.isSuccess && (
            <span className="text-sm text-brand-mint-600">
              {t("data_page.solar_saved_recalc")}
            </span>
          )}
          {save.isError && <span className="text-sm text-red-600">{t("common.error")}</span>}
        </div>
      )}
    </div>
  );
}

// ---------- Закуплений газ (FR-PARAMS-05) ----------

interface GasRow {
  id: string | null; // null → нова, ще не збережена партія
  valid_from: string;
  volume_m3: string;
  price_uah_per_m3: string;
  notes: string;
}

const toGasRow = (p: GasPurchase): GasRow => ({
  id: p.id,
  valid_from: p.valid_from,
  volume_m3: String(p.volume_m3),
  price_uah_per_m3: String(p.price_uah_per_m3),
  notes: p.notes ?? "",
});

function GasPurchasesCard({ canEdit }: { canEdit: boolean }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();

  const purchases = useQuery({
    queryKey: ["gas-purchases"],
    queryFn: () => gasPurchasesApi.list(),
  });
  // Спільний пул на поточний місяць: залишок і середньозважена ціна.
  const pool = useQuery({
    queryKey: ["gas-pool"],
    queryFn: () => gasPurchasesApi.pool(),
  });

  const [rows, setRows] = useState<GasRow[]>([]);
  const [removed, setRemoved] = useState<string[]>([]);

  useEffect(() => {
    if (purchases.data) {
      setRows(purchases.data.map(toGasRow));
      setRemoved([]);
    }
  }, [purchases.data]);

  const setRow = (i: number, patch: Partial<GasRow>) =>
    setRows((s) => s.map((r, j) => (i === j ? { ...r, ...patch } : r)));

  const original = new Map((purchases.data ?? []).map((p) => [p.id, p]));
  const isDirty = (r: GasRow): boolean => {
    if (r.id === null) return true;
    const o = original.get(r.id);
    if (!o) return true;
    return (
      o.valid_from !== r.valid_from ||
      Number(o.volume_m3) !== Number(r.volume_m3) ||
      Number(o.price_uah_per_m3) !== Number(r.price_uah_per_m3) ||
      (o.notes ?? "") !== r.notes
    );
  };

  const rowValid = (r: GasRow) =>
    Boolean(r.valid_from) && Number(r.volume_m3) > 0 && Number(r.price_uah_per_m3) >= 0;

  const hasChanges = removed.length > 0 || rows.some(isDirty);
  const valid = rows.every(rowValid);

  const save = useMutation({
    mutationFn: async () => {
      for (const id of removed) {
        await gasPurchasesApi.remove(id);
      }
      for (const r of rows) {
        if (!isDirty(r)) continue;
        const body = {
          valid_from: r.valid_from,
          volume_m3: Number(r.volume_m3),
          price_uah_per_m3: Number(r.price_uah_per_m3),
          notes: r.notes || null,
        };
        if (r.id === null) await gasPurchasesApi.create(body);
        else await gasPurchasesApi.update(r.id, body);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gas-purchases"] });
      qc.invalidateQueries({ queryKey: ["gas-pool"] });
    },
  });

  const avgPrice = num(pool.data?.avg_price_uah_per_m3 ?? null);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-lg font-semibold text-brand-navy-700">
            {t("data_page.gas_purchases")}
          </h2>
          <div className="text-sm text-slate-600 text-right space-y-0.5">
            <div>
              {t("data_page.gas_remaining_total")}:{" "}
              <span className="font-mono font-semibold">
                {fmt(num(pool.data?.remaining_m3 ?? null))}
              </span>{" "}
              {t("data_page.unit_m3")}
            </div>
            <div>
              {t("data_page.gas_avg_price")}
              {pool.data && ` (${monthLabel(pool.data.month, i18n.language)})`}:{" "}
              <span className="font-mono font-semibold">{fmt(avgPrice, 4)}</span>{" "}
              {avgPrice !== null && t("gas.uah_m3")}
            </div>
          </div>
        </div>
        <CardDescription>{t("data_page.gas_purchases_hint")}</CardDescription>
      </CardHeader>
      <CardContent>
        {purchases.isLoading ? (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500">
                  <th className="p-2 w-[16%]">{t("data_page.gas_valid_from")}</th>
                  <th className="p-2 w-[16%]">{t("data_page.gas_volume")}</th>
                  <th className="p-2 w-[16%]">{t("data_page.gas_price")}</th>
                  <th className="p-2">{t("data_page.notes")}</th>
                  {canEdit && <th className="p-2 w-12" />}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id ?? `new-${i}`} className="border-t border-slate-100 align-top">
                    <td className="p-2">
                      <Input type="date" value={r.valid_from} disabled={!canEdit}
                        onChange={(e) => setRow(i, { valid_from: e.target.value })} />
                    </td>
                    <td className="p-2">
                      <Input type="number" step="1" min="0" value={r.volume_m3} disabled={!canEdit}
                        onChange={(e) => setRow(i, { volume_m3: e.target.value })} />
                    </td>
                    <td className="p-2">
                      <Input type="number" step="0.0001" min="0" value={r.price_uah_per_m3}
                        disabled={!canEdit}
                        onChange={(e) => setRow(i, { price_uah_per_m3: e.target.value })} />
                    </td>
                    <td className="p-2">
                      <Input value={r.notes} disabled={!canEdit}
                        onChange={(e) => setRow(i, { notes: e.target.value })} />
                    </td>
                    {canEdit && (
                      <td className="p-2">
                        <Button variant="ghost" size="icon"
                          onClick={() => {
                            if (r.id !== null) setRemoved((s) => [...s, r.id as string]);
                            setRows((s) => s.filter((_, j) => j !== i));
                          }}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>

            {rows.length === 0 && (
              <div className="text-sm text-slate-500 py-4">{t("data_page.no_gas_purchases")}</div>
            )}
          </div>
        )}

        {canEdit && (
          <div className="flex items-center gap-3 mt-4 flex-wrap">
            <Button variant="outline"
              onClick={() =>
                setRows((s) => [
                  ...s,
                  {
                    id: null,
                    valid_from: new Date().toISOString().slice(0, 10),
                    volume_m3: "",
                    price_uah_per_m3: "",
                    notes: "",
                  },
                ])
              }>
              <Plus className="h-4 w-4" /> {t("data_page.add_gas_purchase")}
            </Button>
            <Button onClick={() => save.mutate()} disabled={!hasChanges || !valid || save.isPending}>
              {save.isPending ? t("common.saving") : t("common.save")}
            </Button>
            {save.isSuccess && !hasChanges && (
              <span className="text-sm text-brand-mint-600">{t("data_page.gas_saved_recalc")}</span>
            )}
            {save.isError && <span className="text-sm text-red-600">{t("common.error")}</span>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
